import { useMemo } from 'react';

import type { DomainQueryOptions } from '@/domain/shared/types';
import { useHealthController_getHealthData } from '@/services/hooks';
import type { HealthOverviewDto } from '@/services/types';
import { dayToString } from '@/utils/date';

import {
    buildHealthOverviewQueryParams,
    type RawHealthOverviewParams,
} from './healthOverviewParams';
import { mapHealthOverviewDtoToDomain } from './overviewMappers';
import type { HealthOverviewData } from './overviewTypes';

export type UseGetHealthOverviewOptions = DomainQueryOptions<HealthOverviewDto>;

type HealthOverviewQuery = ReturnType<typeof useHealthController_getHealthData>;

export function useGetHealthOverview(
    cageId: number,
    params?: RawHealthOverviewParams,
    options?: UseGetHealthOverviewOptions
): Omit<HealthOverviewQuery, 'data'> & { data: HealthOverviewData | undefined } {
    const baseEnabled = options?.enabled !== false && cageId > 0;

    const fromKey = params?.from != null ? dayToString(params.from) : '';
    const toKey = params?.to != null ? dayToString(params.to) : '';

    const apiParams = useMemo(
        () => (params != null ? buildHealthOverviewQueryParams(params) : undefined),
        [params?.daypartTag, fromKey, toKey, params?.fishGroupId]
    );

    const query = useHealthController_getHealthData(cageId, apiParams, {
        ...options,
        enabled: baseEnabled,
    });

    const data = useMemo((): HealthOverviewData | undefined => {
        const raw = query.data?.data;
        return raw ? mapHealthOverviewDtoToDomain(raw) : undefined;
    }, [query.data?.data]);

    return {
        ...query,
        isLoading: query.isPending,
        data,
    };
}
